import { colLabel } from './columnLabels';
import { getWidgetSql, parseSqlSelectKeys } from './buildJoinSql';
import { isCustomColumn, templateFieldKeys, mathFieldKeys } from './tableColumnDisplay';
import {
  isDashboardDataSource,
  dashboardSliceOf,
  dashboardSliceKeys,
  sampleDashboardMetrics,
  sampleUnitSales,
} from './dashboardData';

const SAMPLE_NAMES = ['علی رضایی', 'مریم احمدی', 'حسین کاظمی', 'زهرا محمدی'];
const SAMPLE_JOBS = ['فروشنده', 'سرپرست', 'رییس فروش', 'مدیر شعبه'];
const SAMPLE_DCS = ['فارس', 'تهران', 'اصفهان', 'خراسان'];
const SAMPLE_BRANDS = ['Henkel', 'گلرنگ', 'داروگر', 'شت'];

function extraKeysFromTableColumns(widget) {
  const extra = [];
  (widget?.tableConfig?.columns || []).forEach((c) => {
    const col = typeof c === 'string' ? { key: c } : (c || {});
    templateFieldKeys(col.template).forEach((k) => extra.push(k));
    mathFieldKeys(col.math, []).forEach((k) => extra.push(k));
    [col.key, col.barStartKey, col.barEndKey, col.barValueKey, col.barMaxKey].forEach((k) => {
      if (k && !isCustomColumn({ key: k })) extra.push(k);
    });
  });
  return extra;
}

function selectKeys(widget, extraKeys = []) {
  const fromSelect = (widget?.query?.select || [])
    .map((s) => (typeof s === 'string' ? s : s?.label || s?.key))
    .filter(Boolean);
  const fromFields = (widget?.sqlFields?.fields || [])
    .map((f) => (typeof f === 'string' ? f : f?.key))
    .filter(Boolean);
  const dims = widget?.sqlFields?.dimensions || [];
  const mets = widget?.sqlFields?.metrics || [];
  const fromSql = parseSqlSelectKeys(getWidgetSql(widget));

  let base = [];
  if (fromSql.length) base = fromSql;
  else if (fromFields.length) base = fromFields;
  else if (fromSelect.length) base = fromSelect;
  else if (dims.length || mets.length) base = [...dims, ...mets];

  extraKeysFromTableColumns(widget).forEach((k) => {
    if (k && !base.includes(k)) base.push(k);
  });
  (extraKeys || []).forEach((k) => {
    if (k && !base.includes(k)) base.push(k);
  });
  return base;
}

function sampleValue(key, rowIndex) {
  const k = String(key || '').toLowerCase();
  if (
    k.includes('full_name')
    || k.includes('personnel_name')
    || (k.endsWith('_name') && k.includes('person'))
  ) {
    return SAMPLE_NAMES[rowIndex % SAMPLE_NAMES.length];
  }
  if (k.includes('job')) return SAMPLE_JOBS[rowIndex % SAMPLE_JOBS.length];
  if (k.includes('dc_name') || k === 'dc') return SAMPLE_DCS[rowIndex % SAMPLE_DCS.length];
  if (k.includes('manufacturer') || k === 'name' || k.includes('brand')) {
    return SAMPLE_BRANDS[rowIndex % SAMPLE_BRANDS.length];
  }
  if (k.includes('personnel_code') || (k.includes('code') && !k.includes('dc'))) {
    return `P-${1400 + rowIndex}`;
  }
  if (k.includes('month')) return 5;
  if (k.includes('year')) return 1404;
  if (k.includes('unit')) return 'کارتن';
  if (k.includes('percent') || k.includes('rate') || k.includes('ratio')) {
    return 68 + rowIndex * 7;
  }
  if (k.includes('target')) {
    return (rowIndex + 1) * 2000000;
  }
  if (k.includes('sale') || k.includes('amount') || k.includes('qty') || k.includes('count')) {
    const ratio = Math.min(1.15, 0.32 + rowIndex * 0.16);
    return Math.round((rowIndex + 1) * 2000000 * ratio);
  }
  if (k.includes('date') || k.includes('time') || k.endsWith('_at') || k.includes('created') || k.includes('updated')) {
    const d = new Date(2026, 7, 26, 14, 35, 0);
    d.setDate(d.getDate() + rowIndex);
    return d.toISOString();
  }
  if (k.includes('id')) return 100 + rowIndex;
  return rowIndex + 1;
}

/**
 * Resolve SELECT aliases / configured keys for a widget (paste SQL or visual).
 */
export function resolveWidgetFieldKeys(widget) {
  return selectKeys(widget);
}

/**
 * Fake result shaped like /api/reports/run so designer previews reuse viewer widgets.
 */
export function buildPreviewData(widget, rowCount, extraKeys = []) {
  if (isDashboardDataSource(widget)) {
    const slice = dashboardSliceOf(widget);
    const keys = dashboardSliceKeys(slice);
    const columns = keys.map((key) => ({ key, label: colLabel(key) }));
    if (slice === 'metrics') {
      return {
        columns,
        rows: [sampleDashboardMetrics(0)],
        unit_sales: sampleUnitSales(),
        preview: true,
      };
    }
    const n = rowCount ?? (widget?.type === 'stat_card' || widget?.type === 'gauge' ? 1 : 8);
    const rows = Array.from({ length: n }, (_, i) => {
      const row = {};
      keys.forEach((key) => { row[key] = sampleValue(key, i); });
      if (slice === 'unit_sales') {
        row.unit = row.unit || 'کارتن';
        row.unit_key = row.unit_key || 'carton';
        row.qty = sampleValue('qty', i);
        row.target = sampleValue('target', i);
        row.pct = 70 + i * 8;
      }
      return row;
    });
    return { columns, rows, unit_sales: sampleUnitSales(), preview: true };
  }

  const type = widget?.type;
  const n = rowCount ?? (
    type === 'table' ? 24
      : (type === 'stat_card' || type === 'gauge') ? 1
        : 8
  );
  const keys = selectKeys(widget, extraKeys);
  const columns = keys.map((key) => ({ key, label: colLabel(key) }));
  const rows = keys.length
    ? Array.from({ length: n }, (_, i) => {
      const row = {};
      keys.forEach((key) => { row[key] = sampleValue(key, i); });
      return row;
    })
    : [];
  return { columns, rows, preview: true };
}

export const TABLE_PAGE_SIZE_OPTIONS = [10, 20, 25, 50, 100];

export function resolveTablePageSize(widget) {
  const n = Number(widget?.tableConfig?.pageSize);
  if (Number.isFinite(n) && n >= 5 && n <= 200) return Math.round(n);
  return 20;
}
